import { createFileRoute } from '@tanstack/react-router'
import { useMemo } from 'react'

import type { CountryCodeType } from '@/api/admin-country-types'
import { useCreateSalesChannel } from '@/api/api-mutations'
import {
	preloadListInventoryLocations,
	preloadListSalesChannelGroups,
	preloadListSalesChannelTypes,
	useListInventoryLocations,
	useListSalesChannelGroups
} from '@/api/react-query'
import { Button } from '@/components/base/button'
import { Drawer } from '@/components/base/drawer'
import { Heading } from '@/components/base/heading'
import { Text } from '@/components/base/text'
import { toast, useCreatedToast } from '@/components/base/toast'
import { FormContainer, FormFieldGroup, useAppForm } from '@/components/form/form'
import { RouteModal, RouteModalFormBlocker, useRouteModal } from '@/components/route-modal'
import { useI18n } from '@/i18n/i18n-react'

import { useSalesChannelTypeFieldOptions } from './-sales-channel-type-utils'

export const Route = createFileRoute('/_authenticated/settings/sales-channels/create')({
	component: RouteComponent,
	loader: async ({ context }) => {
		await preloadListSalesChannelTypes(context, {})
		await preloadListSalesChannelGroups(context, {})
		await preloadListInventoryLocations(context, { query: { limit: 100, offset: 0 } })
	},
	staticData: {
		crumb: ({ context: { L } }) => L.Settings.SalesChannels.CreateTitle()
	}
})

function RouteComponent() {
	return (
		<RouteModal asDrawer>
			<NewSalesChannelForm />
		</RouteModal>
	)
}

function NewSalesChannelForm() {
	const { L } = useI18n()
	const { handleSuccess } = useRouteModal()
	const createdToast = useCreatedToast()
	const salesChannelTypeOptions = useSalesChannelTypeFieldOptions()
	const inventoryLocations = useListInventoryLocations({ limit: 100, offset: 0 })
	const inventoryLocationOptions = useMemo(
		() => [
			{ value: '', label: L.Settings.SalesChannels.InventoryLocationNone() },
			...(inventoryLocations.data?.inventoryLocations ?? []).map(location => ({
				value: location.uuid,
				label: location.name
			}))
		],
		[L, inventoryLocations.data?.inventoryLocations]
	)
	const salesChannelGroups = useListSalesChannelGroups()
	const groupOptions = useMemo(
		() => [
			{ value: '', label: L.Settings.SalesChannels.GroupNone() },
			...(salesChannelGroups.data ?? []).map(group => ({ value: group.uuid, label: group.name }))
		],
		[L, salesChannelGroups.data]
	)

	const createSalesChannel = useCreateSalesChannel()

	const form = useAppForm({
		defaultValues: {
			name: '',
			currencyCode: 'ISK',
			countryCode: 'IS',
			salesChannelTypeUuid: '',
			inventoryLocationUuid: '',
			salesChannelGroupUuid: ''
		},
		onSubmit: async ({ value }) => {
			const result = await createSalesChannel({
				body: {
					name: value.name,
					currencyCode: value.currencyCode,
					countryCode: value.countryCode as CountryCodeType,
					salesChannelTypeUuid: value.salesChannelTypeUuid,
					inventoryLocationUuid: value.inventoryLocationUuid || undefined,
					salesChannelGroupUuid: value.salesChannelGroupUuid || undefined
				}
			})
			if (result.isSuccess) {
				createdToast(L.Settings.SalesChannels.CreateSuccess(), {
					to: '/settings/sales-channels/$salesChannelUuid',
					params: { salesChannelUuid: result.response.uuid }
				})
				handleSuccess()
			} else {
				toast.error((result.response as any)?.message ?? L.Error.SomethingWentWrong())
			}
		}
	})

	return (
		<form.AppForm>
			<RouteModalFormBlocker form={form as any} />
			<Drawer.Header>
				<Drawer.Title asChild>
					<Heading>{L.Settings.SalesChannels.CreateTitle()}</Heading>
				</Drawer.Title>
			</Drawer.Header>
			<Drawer.Body className='flex-1 overflow-auto'>
				<FormContainer>
					<FormFieldGroup>
						<form.AppField
							name='name'
							validators={{ onChange: ({ value }) => (!value ? L.General.Required() : undefined) }}
							children={field => (
								<field.TextField
									autoFocus
									label={L.Fields.Name()}
									placeholder={L.Settings.SalesChannels.Create.NamePlaceholder()}
								/>
							)}
						/>
					</FormFieldGroup>
					<FormFieldGroup>
						<div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
							<form.AppField
								name='currencyCode'
								children={field => (
									<field.SelectField
										label={L.Settings.SalesChannels.Column.CurrencyCode()}
										options={[
											{ value: 'ISK', label: 'ISK - Icelandic Króna' },
											{ value: 'DKK', label: 'DKK - Danish Krone' },
											{ value: 'NOK', label: 'NOK - Norwegian Krone' },
											{ value: 'SEK', label: 'SEK - Swedish Krona' },
											{ value: 'EUR', label: 'EUR - Euro' }
										]}
									/>
								)}
							/>
							<form.AppField
								name='countryCode'
								children={field => (
									<field.SelectField
										label={L.Settings.SalesChannels.Column.CountryCode()}
										options={[
											{ value: 'IS', label: 'Iceland' },
											{ value: 'DK', label: 'Denmark' },
											{ value: 'NO', label: 'Norway' },
											{ value: 'SE', label: 'Sweden' },
											{ value: 'FI', label: 'Finland' }
										]}
									/>
								)}
							/>
						</div>
					</FormFieldGroup>
					<FormFieldGroup>
						<form.AppField
							name='salesChannelTypeUuid'
							children={field => (
								<field.SelectField
									label={L.Settings.SalesChannels.Column.SalesChannelType()}
									options={salesChannelTypeOptions}
								/>
							)}
						/>
						<Text size='small' className='text-ui-fg-muted'>
							{L.Settings.SalesChannels.TypeOptionalHint()}
						</Text>
						<form.AppField
							name='inventoryLocationUuid'
							children={field => (
								<field.SelectField
									label={L.Settings.SalesChannels.Column.InventoryLocation()}
									options={inventoryLocationOptions}
									optionsLoading={inventoryLocations.isLoading}
								/>
							)}
						/>
						<Text size='small' className='text-ui-fg-muted'>
							{L.Settings.SalesChannels.InventoryLocationOptionalHint()}
						</Text>
						<form.AppField
							name='salesChannelGroupUuid'
							children={field => (
								<field.SelectField
									label={L.Settings.SalesChannels.Column.Group()}
									options={groupOptions}
									optionsLoading={salesChannelGroups.isLoading}
								/>
							)}
						/>
						<Text size='small' className='text-ui-fg-muted'>
							{L.Settings.SalesChannels.GroupOptionalHint()}
						</Text>
					</FormFieldGroup>
				</FormContainer>
			</Drawer.Body>
			<Drawer.Footer>
				<div className='flex items-center justify-end gap-x-2'>
					<Drawer.Close asChild>
						<Button variant='secondary' size='small'>
							{L.General.Cancel()}
						</Button>
					</Drawer.Close>
					<form.CreateButton />
				</div>
			</Drawer.Footer>
		</form.AppForm>
	)
}
